import type { AttachableInvoice, CommercialAgreementDetail } from '../types';
import { sendWithReplayKey, submitManualPayment } from './manual-payment-request';
import dayjs from 'dayjs';
import { http } from './http';
import {
  DashboardOverview,
  AvailableSlot,
  Vehicle,
  VehiclePayload,
  VehiclesResponse,
  Booking,
  School,
  CandidatesOverview,
  CandidateListItem,
  CreateCandidatePayload,
  UpdateCandidatePayload,
  CandidateDetail,
  CandidateFinance,
  ReadinessScore,
  CandidateReadinessItem,
  SchoolSettingsResponse,
  PlanningColors,
  UpdateSchoolSettingsPayload,
  SchoolProfile,
  UpdateSchoolProfilePayload,
  SchoolAccessSettings,
  UpdateSchoolAccessSettingsPayload,
  TestRdvPermisResult,
  RealtimeAttemptsResponse,
  ExamsOverview,
  ExamItem,
  ManualExamResult,
  CandidatePortalAccess,
  CandidateStatusPreview,
  AuditTimelineResponse,
  ComplianceOverview,
  CandidateExportPayload,
  NotificationSettings,
  NotificationSettingsUpdate,
  NotificationEvent,
  LivretReferentials,
  LivretMetadataPayload,
  LivretCraResponse,
  LivretDeclarationSettings,
  RdvCenter,
  RdvCenterSearchPayload,
  LastExamResult,
  EtpsSummaryResponse,
  EtpsThresholdResponse,
  RequestItem,
  RequestDiagnostic,
  CreateRequestPayload,
  SchoolPaniersResponse,
  VehicleAvailability,
  CandidateAvailability,
  InstructorAvailability,
  LessonsStats,
  Lesson,
  CreateLessonPayload,
  CreateLessonGroupPayload,
  UpdateLessonPayload,
  Instructor,
  InstructorStat,
  InstructorAccount,
  TeamMember,
  TeamMemberPayload,
  InstructorInvoice,
  DocumentRequest,
  DocumentTypeOption,
  NetworkOverview,
  CreateRecurringLessonPayload,
  RecurringLessonResult,
  InstructorWeekTemplateEvent,
  CatalogItem,
  CatalogFormula,
  CatalogMeta,
  CreateCatalogItemPayload,
  CreateCatalogFormulaPayload,
  Quote,
  Invoice,
  ElectronicReadiness,
  InvoiceElectronic,
  CreateQuotePayload,
  ListDocumentsQuery,
  ExportDocumentsQuery,
  BillingCandidate,
} from '../types';

type SlotResponse = {
  id: string;
  providerSlotId: string;
  centerId: string;
  startsAt: string;
  endsAt: string;
  vehicleType: string;
  status: string;
  metadata?: Record<string, any>;
};

type StartRdvOauthResponse = {
  authorizationUrl: string;
};

/** Moyen de paiement en ligne proposable aux élèves (Réglages › Paiements, J42). */
export type OnlinePaymentMethod = {
  type: 'card' | 'klarna' | 'paypal';
  label: string;
  /** Activé par l'auto-école. */
  enabled: boolean;
  /** État sur son compte Stripe ; `unknown` : compte absent ou illisible. */
  stripeStatus: 'active' | 'pending' | 'inactive' | 'unrequested' | 'unknown';
};

export const schoolApi = {
  async fetchCurrent(): Promise<School> {
    const { data } = await http.get<School>('/v1/schools/me');
    return data;
  },

  async fetchDashboard(params?: {
    timeframe?: string;
    from?: string;
    to?: string;
    vehicleType?: string;
  }): Promise<DashboardOverview> {
    const { data } = await http.get<DashboardOverview>(
      '/v1/schools/dashboard',
      { params },
    );

    const summary = data.summary ?? {
      candidates: {
        total: 0,
        eligible: 0,
        withoutMandate: 0,
        penalty48h: 0,
      },
      capturedSlots: { total: 0, today: 0, thisWeek: 0 },
      successRate: { overall: null, last24h: null, last7d: null },
      failedAttempts: {
        total: 0,
        breakdown: {
          penalty48h: 0,
          instructorLimit: 0,
          rateLimit: 0,
          missingMandate: 0,
          other: 0,
        },
      },
    };

    const serviceHealth = data.serviceHealth ?? {
      status: 'unknown',
      message: 'Aucune donnee de sante disponible',
      severity: 'warning' as const,
      checkedAt: null,
    };

    return {
      summary: {
        candidates: {
          total: summary.candidates?.total ?? 0,
          eligible: summary.candidates?.eligible ?? 0,
          withoutMandate: summary.candidates?.withoutMandate ?? 0,
          penalty48h: summary.candidates?.penalty48h ?? 0,
        },
        capturedSlots: {
          total: summary.capturedSlots?.total ?? 0,
          today: summary.capturedSlots?.today ?? 0,
          thisWeek: summary.capturedSlots?.thisWeek ?? 0,
        },
        successRate: {
          overall: summary.successRate?.overall ?? null,
          last24h: summary.successRate?.last24h ?? null,
          last7d: summary.successRate?.last7d ?? null,
        },
        requestStats: {
          fulfilled: summary.requestStats?.fulfilled ?? 0,
          canceled: summary.requestStats?.canceled ?? 0,
          active: summary.requestStats?.active ?? 0,
        },
        failedAttempts: {
          total: summary.failedAttempts?.total ?? 0,
          breakdown: {
            penalty48h: summary.failedAttempts?.breakdown?.penalty48h ?? 0,
            instructorLimit:
              summary.failedAttempts?.breakdown?.instructorLimit ?? 0,
            rateLimit: summary.failedAttempts?.breakdown?.rateLimit ?? 0,
            missingMandate:
              summary.failedAttempts?.breakdown?.missingMandate ?? 0,
            other: summary.failedAttempts?.breakdown?.other ?? 0,
          },
        },
      },
      serviceHealth: {
        status: serviceHealth.status,
        message: serviceHealth.message,
        severity: serviceHealth.severity,
        checkedAt: serviceHealth.checkedAt ?? null,
      },
      systemMessages: data.systemMessages ?? [],
      nextOpeningWindow: data.nextOpeningWindow ?? null,
      quickActions: data.quickActions ?? [],
      filters: {
        timeframe: data.filters?.timeframe ?? params?.timeframe ?? 'week',
        from: data.filters?.from ?? null,
        to: data.filters?.to ?? params?.to ?? new Date().toISOString(),
        vehicleType: data.filters?.vehicleType ?? params?.vehicleType ?? null,
      },
    };
  },

  async fetchSlots(params: {
    centerId?: string;
    fromDate?: string;
    toDate?: string;
    groupePermis?: 'A' | 'B' | 'B auto' | 'CE';
  }) {
    const { data } = await http.get<SlotResponse[]>('/v1/slots', {
      params,
    });
    return data.map<AvailableSlot>((slot) => {
      const starts = dayjs(slot.startsAt);
      const ends = dayjs(slot.endsAt);
      const centerLabel =
        slot.metadata?.centerLabel ??
        slot.metadata?.center_name ??
        slot.metadata?.nomCentre ??
        slot.metadata?.nom_centre ??
        slot.metadata?.centreNom ??
        slot.metadata?.centre_nom ??
        slot.metadata?.centerName ??
        slot.metadata?.center_name ??
        slot.metadata?.centreName ??
        slot.metadata?.centre_name ??
        slot.metadata?.centre?.nomCentre ??
        slot.metadata?.centre?.nom ??
        slot.metadata?.centre?.libelle ??
        slot.metadata?.centre?.name ??
        slot.metadata?.center?.nomCentre ??
        slot.metadata?.center?.nom ??
        slot.metadata?.center?.libelle ??
        slot.metadata?.center?.name ??
        slot.centerId;
      const remaining =
        typeof slot.metadata?.remaining === 'number'
          ? slot.metadata.remaining
          : (slot.metadata?.remainingSeats ?? 1);

      return {
        id: slot.id,
        providerSlotId: slot.providerSlotId,
        centerId: slot.centerId,
        centerLabel,
        vehicleType: slot.vehicleType,
        startsAt: slot.startsAt,
        endsAt: slot.endsAt,
        startsAtLabel: starts.isValid()
          ? starts.format('DD/MM/YYYY HH:mm')
          : slot.startsAt,
        endsAtLabel: ends.isValid()
          ? ends.format('DD/MM/YYYY HH:mm')
          : slot.endsAt,
        remaining: remaining ?? 1,
        metadata: slot.metadata ?? {},
      };
    });
  },

  async fetchBookings(): Promise<Booking[]> {
    const { data } = await http.get<Booking[]>('/v1/bookings');
    return data;
  },

  async createBooking(payload: {
    slotId: string;
    candidateId: string;
    livretMetadata?: LivretMetadataPayload;
  }): Promise<Booking> {
    const { data } = await http.post<Booking>('/v1/bookings', payload);
    return data;
  },

  async holdSlot(slotId: string) {
    await http.post('/v1/slots/hold', { slotId });
  },

  async fetchCandidates(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    groupePermis?: 'A' | 'B' | 'B auto' | 'CE' | 'tous';
    // Filtres de la liste « Tous les élèves » (un choix par filtre, cumulables).
    evaluation?: string;
    contract?: string;
    neph?: string;
    payment?: string;
    availability?: string;
    funder?: string;
    /** Archivés masqués (`exclude`) ou seuls (`only`) ; absent : inclus. */
    archived?: string;
  }): Promise<CandidatesOverview> {
    const { data } = await http.get<CandidatesOverview>('/v1/candidates', {
      params,
    });
    return {
      summary: {
        total: data.summary?.total ?? 0,
        eligible: data.summary?.eligible ?? 0,
        nonMandated: data.summary?.nonMandated ?? 0,
        penalty48h: data.summary?.penalty48h ?? 0,
      },
      items: data.items ?? [],
      meta: {
        page: data.meta?.page ?? 1,
        pageSize: data.meta?.pageSize ?? params.pageSize ?? 10,
        totalItems: data.meta?.totalItems ?? data.items?.length ?? 0,
        totalPages: data.meta?.totalPages ?? 1,
      },
    };
  },

  async fetchAllCandidates(params: {
    groupePermis?: 'A' | 'B' | 'B auto' | 'CE';
  }): Promise<CandidateListItem[]> {
    const { data } = await http.get<CandidateListItem[]>(
      '/v1/candidates/planning',
      { params: { groupePermis: params.groupePermis } },
    );
    return data;
  },

  async createCandidate(payload: CreateCandidatePayload) {
    const { data } = await http.post('/v1/candidates', payload);
    return data;
  },

  async createCandidateMandate(
    candidateId: string,
  ): Promise<{ hasMandate: boolean }> {
    const { data } = await http.post<{ hasMandate: boolean }>(
      `/v1/candidates/${candidateId}/mandate`,
    );
    return data;
  },

  /** Dernier examen pratique lu chez RdvPermis ; `null` quand RdvPermis n'en connaît aucun (réponse vide). */
  async fetchCandidateLastExam(candidateId: string) {
    const { data } = await http.get<LastExamResult | ''>(`/v1/candidates/${candidateId}/last-exam`);
    return data || null;
  },

  async fetchCandidate(id: string): Promise<CandidateDetail> {
    const { data } = await http.get<CandidateDetail>(`/v1/candidates/${id}`);
    return {
      ...data,
      email: data.email ?? undefined,
      phoneNumber: data.phoneNumber ?? undefined,
      instructorName: data.instructorName ?? undefined,
      penaltyUntil: data.penaltyUntil ?? undefined,
      preferredCenters: data.preferredCenters ?? [],
      availabilityDays: data.availabilityDays ?? [],
      examHistory: data.examHistory ?? [],
    };
  },

  async fetchReferenceInstructors(): Promise<{ id: string; label: string }[]> {
    const { data } = await http.get('/v1/candidates/reference-instructors');
    return data;
  },

  async updateCandidate(id: string, payload: UpdateCandidatePayload & { version: number }) {
    const { data } = await http.patch(`/v1/candidates/${id}`, payload);
    return data;
  },

  /** Effets d'un archivage ou d'une réactivation ; 409 avec le motif s'il n'est pas possible. */
  async previewCandidateStatus(
    id: string,
    status: 'active' | 'archived',
  ): Promise<CandidateStatusPreview> {
    const { data } = await http.post(`/v1/candidates/${id}/status/preview`, {
      status,
    });
    return data;
  },

  /** Accès de l'élève à son espace : jamais connecté ou non, suivi de la dernière invitation renvoyée. */
  async getCandidatePortalAccess(id: string): Promise<CandidatePortalAccess> {
    const { data } = await http.get(`/v1/candidates/${id}/portal-access`);
    return data;
  },

  /** E-mail de connexion à renvoyer (destinataire, objet, message) ; 409 avec le motif s'il n'est pas possible. */
  async previewCandidateInvitation(id: string): Promise<CandidateStatusPreview> {
    const { data } = await http.post(`/v1/candidates/${id}/invitation/preview`);
    return data;
  },

  /** Renvoie l'e-mail de connexion, sur la version vue à l'aperçu : mis en file, il part dans la minute. */
  async resendCandidateInvitation(
    id: string,
    version: number,
  ): Promise<{ id: string; invitation: CandidatePortalAccess['lastInvitation'] }> {
    const { data } = await http.post(`/v1/candidates/${id}/invitation`, { version });
    return data;
  },

  /** Archive ou réactive l'élève, sur la version vue à l'aperçu. */
  async changeCandidateStatus(
    id: string,
    status: 'active' | 'archived',
    version: number,
  ): Promise<{ id: string; status: string }> {
    const { data } = await http.post(`/v1/candidates/${id}/status`, {
      status,
      version,
    });
    return data;
  },

  async deleteCandidate(id: string) {
    await http.delete(`/v1/candidates/${id}`);
  },

  async fetchCandidateReadinessScore(
    candidateId: string,
  ): Promise<ReadinessScore> {
    const { data } = await http.get<ReadinessScore>(
      `/v1/candidates/${candidateId}/readiness-score`,
    );
    return data;
  },

  async fetchCandidateFinance(candidateId: string): Promise<CandidateFinance> {
    const { data } = await http.get<CandidateFinance>(
      `/v1/candidates/${candidateId}/finance`,
    );
    return data;
  },

  async fetchCandidateLessonBilans(
    candidateId: string,
  ): Promise<import('../types').CandidateLessonBilan[]> {
    const { data } = await http.get<import('../types').CandidateLessonBilan[]>(
      `/v1/candidates/${candidateId}/lesson-bilans`,
    );
    return data ?? [];
  },

  // Émargement d'une leçon (lecture staff) : statut + image de signature.
  async fetchLessonAttendance(
    lessonId: string,
  ): Promise<import('../types').LessonAttendance> {
    const { data } = await http.get<import('../types').LessonAttendance>(
      `/v1/lessons/${lessonId}/attendance`,
    );
    return data;
  },

  // Absence de l'élève signalée (évaluation de départ, rendez-vous… : pas d'émargement).
  async reportLessonAbsence(
    lessonId: string,
    version: number,
  ): Promise<{ id: string; version: number; absenceReportedAt: string | null }> {
    const { data } = await http.post<{
      id: string;
      version: number;
      absenceReportedAt: string | null;
    }>(`/v1/lessons/${lessonId}/absence`, { version });
    return data;
  },

  // Émargement par le bureau quand le moniteur l'a oublié : signature de l'élève à l'écran du bureau,
  // ou présence attestée sans signature avec un motif (gérant, responsable, secrétariat).
  async recordOfficeAttendance(
    lessonId: string,
    body:
      | { version: number; method: 'office_signature'; signature: string }
      | { version: number; method: 'office_attestation'; reason: string },
  ): Promise<{ id: string; version: number; attendance: import('../types').LessonAttendance }> {
    const { data } = await http.post<{
      id: string;
      version: number;
      attendance: import('../types').LessonAttendance;
    }>(`/v1/lessons/${lessonId}/attendance`, body);
    return data;
  },

  // Feuille d'émargement (PDF) d'un élève.
  async exportAttendanceSheet(candidateId: string): Promise<Blob> {
    const response = await http.get(
      `/v1/candidates/${candidateId}/attendance-sheet`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  // Document AAC / CS d'un élève (PDF pré-rempli, cachet de l'école).
  async downloadAccompaniedDocument(candidateId: string, document: string): Promise<Blob> {
    const response = await http.get(
      `/v1/candidates/${candidateId}/accompanied-driving/${document}`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  // Feuille d'émargement (PDF) d'un moniteur sur une période.
  async exportInstructorAttendanceSheet(
    instructorId: string,
    from?: string,
    to?: string,
  ): Promise<Blob> {
    const response = await http.get('/v1/lessons/attendance-sheet', {
      params: { instructorId, from, to },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // Livret d'apprentissage — lecture seule côté staff.
  async fetchCandidateLearningBooklet(
    candidateId: string,
  ): Promise<import('../types').LearningBooklet> {
    const { data } = await http.get<import('../types').LearningBooklet>(
      `/v1/candidates/${candidateId}/learning-booklet`,
    );
    return data;
  },

  // Évaluation de départ.
  async fetchCandidateStartingEvaluation(
    candidateId: string,
  ): Promise<import('../types').StartingEvaluationResult> {
    const { data } = await http.get<
      import('../types').StartingEvaluationResult
    >(`/v1/candidates/${candidateId}/starting-evaluation`);
    return data;
  },

  /** Échéances de l'échéancier de l'élève ; vide s'il n'en a pas (contrat : modalité de paiement). */
  async fetchCandidateSchedule(
    candidateId: string,
  ): Promise<{ dueOn: string; amountCents: number }[]> {
    const { data } = await http.get<{
      revision: unknown;
      lines?: { dueOn: string; amountCents: number }[];
    }>(`/v1/candidate-receivables/${candidateId}`);
    return data?.revision && Array.isArray(data.lines) ? data.lines : [];
  },

  /** Prises en charge de l'élève (financeur, type, état, montant). */
  async fetchCandidateFundings(candidateId: string): Promise<
    { payer: string; kind: string; status: string; amountCents: number }[]
  > {
    const { data } = await http.get<{
      items?: {
        snapshot: {
          payer: string;
          kind: string;
          status: string;
          amountCents: number;
        };
      }[];
    }>(`/v1/candidate-fundings/${candidateId}`);
    return Array.isArray(data?.items)
      ? data.items.map((item) => item.snapshot)
      : [];
  },

  async requestCandidateStartingEvaluation(
    candidateId: string,
    restart = false,
  ): Promise<{ status: string; notificationMessage?: string }> {
    const { data } = await http.post<{ status: string; notificationMessage?: string }>(
      `/v1/candidates/${candidateId}/starting-evaluation/${restart ? 'restart' : 'request'}`,
      {},
    );
    return data;
  },

  async fetchReadinessScores(
    groupePermis?: string,
  ): Promise<CandidateReadinessItem[]> {
    const { data } = await http.get<CandidateReadinessItem[]>(
      '/v1/candidates/readiness-scores',
      { params: groupePermis ? { groupePermis } : undefined },
    );
    return data ?? [];
  },

  async fetchSmartQueue(options?: {
    groupePermis?: string;
    limit?: number;
  }): Promise<import('../types').SmartQueueResult> {
    const { data } = await http.get<import('../types').SmartQueueResult>(
      '/v1/candidates/smart-queue',
      { params: options },
    );
    return data;
  },

  async pauseCandidates(candidateIds: string[], pauseDays?: number) {
    const { data } = await http.post(`/v1/candidates/bulk/pause`, {
      candidateIds,
      pauseDays,
    });
    return data as { updated: number; penaltyUntil: string };
  },

  async fetchSettings(): Promise<SchoolSettingsResponse> {
    const { data } = await http.get<SchoolSettingsResponse>(
      '/v1/schools/me/settings',
    );
    return data;
  },

  /** Seuls les champs envoyés changent (PATCH partiel). */
  async updateSettings(
    payload: Partial<UpdateSchoolSettingsPayload>,
  ): Promise<SchoolSettingsResponse> {
    const { data } = await http.patch<SchoolSettingsResponse>(
      '/v1/schools/me/settings',
      payload,
    );
    return data;
  },

  /**
   * Réglages › App moniteur : les moniteurs ouvrent et ferment leurs créneaux depuis l'application. Seul réglage que
   * le secrétariat change aussi (route propre, sans accès aux autres réglages) ; rend les réglages relus.
   */
  async setInstructorOpenSlots(enabled: boolean): Promise<SchoolSettingsResponse> {
    const { data } = await http.put<SchoolSettingsResponse>('/v1/schools/me/settings/instructor-open-slots', { enabled });
    return data;
  },

  /** Couleurs des fiches du planning (seul champ envoyé ; null = fiches blanches). */
  async updatePlanningColors(planningColors: PlanningColors | null) {
    const { data } = await http.patch('/v1/schools/me/settings', { planningColors });
    return data;
  },

  async fetchProfile(): Promise<SchoolProfile> {
    const { data } = await http.get<SchoolProfile>('/v1/schools/me/profile');
    return data;
  },

  async updateProfile(
    payload: UpdateSchoolProfilePayload,
  ): Promise<SchoolProfile> {
    const { data } = await http.patch<SchoolProfile>(
      '/v1/schools/me/profile',
      payload,
    );
    return data;
  },

  async uploadProfileAsset(
    kind: 'logo' | 'cachet',
    file: File,
  ): Promise<SchoolProfile> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await http.post<SchoolProfile>(
      `/v1/schools/me/profile/${kind}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async removeProfileAsset(kind: 'logo' | 'cachet'): Promise<SchoolProfile> {
    const { data } = await http.delete<SchoolProfile>(
      `/v1/schools/me/profile/${kind}`,
    );
    return data;
  },

  async fetchAccessSettings(): Promise<SchoolAccessSettings> {
    const { data } = await http.get<SchoolAccessSettings>(
      '/v1/schools/me/access-settings',
    );
    return data;
  },

  async updateAccessSettings(
    payload: UpdateSchoolAccessSettingsPayload,
  ): Promise<SchoolAccessSettings> {
    const { data } = await http.patch<SchoolAccessSettings>(
      '/v1/schools/me/access-settings',
      payload,
    );
    return data;
  },

  async startRdvPermisOauth(payload?: { redirectUri?: string }) {
    const { data } = await http.post<StartRdvOauthResponse>(
      '/v1/integrations/rdv/oauth/start',
      payload ?? {},
    );
    return data;
  },

  async checkReservationAccess(): Promise<{ status: 'valid' | 'reconnect_required'; expiresAt?: string }> {
    const { data } = await http.get<{ status: 'valid' | 'reconnect_required'; expiresAt?: string }>('/v1/integrations/rdv-permis/reservation-access');
    return data;
  },

  async disconnectRdvPermis(): Promise<{ disconnected: boolean; remoteRevoked: boolean; localSecretsDeleted: boolean }> {
    const { data } = await http.post('/v1/integrations/rdv/oauth/disconnect');
    return data;
  },

  async testRdvPermisConnection(): Promise<TestRdvPermisResult> {
    const { data } = await http.post<TestRdvPermisResult>(
      '/v1/integrations/rdv-permis/test',
      {},
    );
    return data;
  },

  async syncRdvPermisSlotsForSchool(): Promise<number> {
    const { data } = await http.post<number>(
      '/v1/integrations/rdv-permis/sync-school',
      {},
    );
    return typeof data === 'number' ? data : 0;
  },

  async searchRdvCenters(
    payload: RdvCenterSearchPayload,
  ): Promise<RdvCenter[]> {
    const { data } = await http.post<RdvCenter[]>(
      '/v1/integrations/rdv-permis/centres/recherche',
      payload,
    );
    return data ?? [];
  },

  async saveFavoriteCenters(payload: {
    groupePermis: 'A' | 'B' | 'CE';
    centresFavorisInput: Array<{ centreId: string; priorite: number }>;
  }) {
    const { data } = await http.post(
      '/v1/integrations/rdv-permis/centres-favoris/save',
      payload,
    );
    return data;
  },

  async listFavoriteCenters() {
    const { data } = await http.post(
      '/v1/integrations/rdv-permis/centres-favoris/list',
    );
    return data as Array<{
      groupePermis: string;
      centreId: string;
      nomCentre: string;
      codeDepartement: string;
      nomDepartement?: string;
      priorite: number;
    }>;
  },

  async fetchRealtimeAttempts(params?: {
    search?: string;
    result?: 'all' | 'success' | 'failure';
    department?: string;
    limit?: number;
  }) {
    const { data } = await http.get<RealtimeAttemptsResponse>(
      '/v1/schools/realtime',
      { params },
    );
    return data;
  },

  async fetchExams(params?: {
    groupePermis?: 'A' | 'B' | 'CE';
    dateDebut?: string;
    creneauSansCandidat?: boolean;
  }): Promise<ExamsOverview> {
    const { data } = await http.get<ExamsOverview>('/v1/exams', { params });
    return data;
  },

  async cancelExam(id: string, reason?: string): Promise<ExamItem> {
    const { data } = await http.patch<ExamItem>(`/v1/exams/${id}/cancel`, {
      reason,
    });
    return data;
  },

  async exportExamsCsv(scope?: 'upcoming' | 'history') {
    const response = await http.get('/v1/exams/export', {
      params: { scope },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async exportExamsPdf() {
    const response = await http.get('/v1/exams/export/pdf', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /** Récapitulatif de convocation préparé par l'école (PDF) ; l'officielle vient de RdvPermis. */
  async downloadExamConvocation(examId: string): Promise<Blob> {
    const response = await http.get(
      `/v1/exams/${encodeURIComponent(examId)}/convocation`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  /** Dépose le récapitulatif dans l'espace élève et le prévient (sans doublon). */
  async sendExamConvocation(examId: string): Promise<{
    id: string;
    sentAt: string;
    alreadySent: boolean;
    candidateName: string;
  }> {
    const { data } = await http.post(
      `/v1/exams/${encodeURIComponent(examId)}/convocation/send`,
    );
    return data;
  },

  /** Résultat saisi par l'école (Validé, Ajourné, Absent) ou effacé (`null`). */
  async setExamResult(
    recordId: string,
    result: ManualExamResult | null,
  ): Promise<{ id: string; result: ManualExamResult | null }> {
    const { data } = await http.patch(
      `/v1/exams/history/${encodeURIComponent(recordId)}/result`,
      { result },
    );
    return data;
  },

  /** Demande à RdvPermis les résultats en attente (élèves mandatés). */
  async syncExamResults(): Promise<{
    checked: number;
    updated: number;
    added: number;
    interrupted: boolean;
  }> {
    const { data } = await http.post('/v1/exams/results/sync');
    return data;
  },

  async permuterExams(examenId1: string, examenId2: string): Promise<void> {
    await http.post('/v1/exams/permutation', { examenId1, examenId2 });
  },

  async remplacerExam(
    examenId: string,
    candidatRemplacantId: string,
  ): Promise<void> {
    await http.post('/v1/exams/remplacement', {
      examenId,
      candidatRemplacantId,
    });
  },

  async fetchPermutableExams(examenId: string): Promise<ExamItem[]> {
    const { data } = await http.get<ExamItem[]>('/v1/exams/permutables', {
      params: { examenId },
    });
    return data ?? [];
  },

  async fetchAuditTimeline(params?: {
    search?: string;
    type?: string;
    limit?: number;
  }): Promise<AuditTimelineResponse> {
    const { data } = await http.get<AuditTimelineResponse>('/v1/audit/me', {
      params,
    });
    return data;
  },

  async fetchComplianceOverview(): Promise<ComplianceOverview> {
    const { data } = await http.get<ComplianceOverview>(
      '/v1/compliance/overview',
    );
    return data;
  },

  async exportCandidateData(externalRef: string) {
    const { data } = await http.post<CandidateExportPayload>(
      '/v1/compliance/candidates/export',
      {
        externalRef,
      },
    );
    return data;
  },

  async requestCandidateErasure(externalRef: string, requestId: string) {
    const { data } = await http.post<{
      id: string;
      deleted: false;
      status: string;
    }>('/v1/compliance/privacy-requests', { externalRef, requestId });
    return data;
  },
  async listPrivacyRequests() {
    const { data } = await http.get<
      Array<{ id: string; created_at: string; action: string | null }>
    >('/v1/compliance/privacy-requests');
    return data;
  },
  async processPrivacyRequest(
    id: string,
    action: 'preferences_cleared' | 'retained',
    policyReference: string,
  ) {
    const { data } = await http.post(
      '/v1/compliance/privacy-requests/' + id + '/decision',
      { action, policyReference },
    );
    return data;
  },

  async fetchEtpsSummary(params?: {
    groupePermis?: 'A' | 'B' | 'CE';
  }): Promise<EtpsSummaryResponse> {
    const { data } = await http.get<EtpsSummaryResponse>('/v1/etps/summary', {
      params,
    });
    return data;
  },

  async fetchEtpsThreshold(params: {
    codeDepartement: string;
    groupePermis?: 'A' | 'B' | 'CE';
    date?: string;
  }): Promise<EtpsThresholdResponse> {
    const { data } = await http.get<EtpsThresholdResponse>(
      '/v1/etps/threshold',
      {
        params,
      },
    );
    return data;
  },

  async searchMandatedCandidates(payload: {
    query?: string;
    numeroDossier?: string;
    groupePermis?: string;
    page?: number;
    perPage?: number;
  }): Promise<{
    items: Array<{
      id: string;
      name: string;
      dossier: string | null;
      email: string | null;
      groupePermis?: string | null;
    }>;
    page: number;
    totalPages: number;
    totalItems?: number;
    perPage: number;
  }> {
    const { data } = await http.post(
      '/v1/integrations/rdv-permis/candidats-mandats/recherche',
      payload,
    );
    return data;
  },

  async fetchNotificationSettings(): Promise<NotificationSettings> {
    const { data } = await http.get<NotificationSettings>(
      '/v1/notifications/settings',
    );
    return data;
  },

  async updateNotificationSettings(
    payload: NotificationSettingsUpdate,
  ): Promise<NotificationSettings> {
    const { data } = await http.patch<NotificationSettings>(
      '/v1/notifications/settings',
      payload,
    );
    return data;
  },

  async fetchNotificationLogs(): Promise<NotificationEvent[]> {
    const { data } = await http.get<NotificationEvent[]>(
      '/v1/notifications/logs',
    );
    return data;
  },

  async sendNotificationTestEmail(to?: string) {
    const { data } = await http.post('/v1/notifications/test', { to });
    return data;
  },

  async fetchAccessibleSchools(): Promise<School[]> {
    const { data } = await http.get<School[]>('/v1/schools/access');
    return data;
  },

  async fetchLivretReferentials(): Promise<LivretReferentials> {
    const { data } = await http.get<LivretReferentials>(
      '/v1/livret/referentials',
    );
    return data;
  },

  async fetchLivretCra(month: string): Promise<LivretCraResponse> {
    const { data } = await http.get<LivretCraResponse>('/v1/livret/cra', {
      params: { month },
    });
    return data;
  },

  /** Livret d'un contrat (leçons enregistrées localement), en PDF ou en CSV. */
  async exportLivretContract(
    contractId: string,
    format: 'pdf' | 'csv',
  ): Promise<{ blob: Blob; filename: string | null }> {
    const res = await http.get<Blob>(
      `/v1/livret/contracts/${contractId}/export`,
      { params: { format }, responseType: 'blob' },
    );
    const disposition = String(res.headers['content-disposition'] ?? '');
    return {
      blob: res.data,
      filename: /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? null,
    };
  },

  /** Simulateur d'une leçon, quand l'école en a déclaré plusieurs à RdvPermis ; `null` efface le choix. */
  async chooseLivretSimulator(lessonId: string, agreement: string | null): Promise<{ lessonId: string; agreement: string | null }> {
    const { data } = await http.put<{ lessonId: string; agreement: string | null }>(
      `/v1/livret/declarations/lessons/${encodeURIComponent(lessonId)}/simulator`,
      { agreement },
    );
    return data;
  },
  /** Valide chez RdvPermis la formation qualifiante d'un élève (7 h déclarées). */
  async validateLivretQualifying(candidateId: string): Promise<{ candidateId: string; status: 'validated'; minutes: number }> {
    const { data } = await http.post<{ candidateId: string; status: 'validated'; minutes: number }>(
      `/v1/livret/declarations/qualifying/${encodeURIComponent(candidateId)}/validate`,
    );
    return data;
  },
  /** Déclare le mois à la main : ses leçons partent au livret officiel, les leçons annulées ou modifiées en sont retirées (en tâche de fond). */
  async declareLivretMonth(month: string): Promise<{ queued: number; withdrawals: number }> {
    const { data } = await http.post<{ queued: number; withdrawals: number }>('/v1/livret/declarations', { month });
    return data;
  },
  /** Réglage de la déclaration de l'école : automatique, ou à la main seulement (par défaut). */
  async fetchLivretDeclarationSettings(): Promise<LivretDeclarationSettings> {
    const { data } = await http.get<LivretDeclarationSettings>('/v1/livret/declarations/settings');
    return data;
  },
  /** Active ou coupe la déclaration automatique de l'école. Gérant et responsables. */
  async updateLivretDeclarationSettings(automatic: boolean): Promise<LivretDeclarationSettings> {
    const { data } = await http.put<LivretDeclarationSettings>('/v1/livret/declarations/settings', { automatic });
    return data;
  },

  /** Recopie le livret officiel (RdvPermis) : 30 derniers jours et semaine à venir. Gérant et responsables. */
  async syncLivret(): Promise<void> {
    await http.post('/v1/compliance/livret/sync');
  },

  async fetchDebugPaniers(params?: {
    page?: number;
    parPage?: number;
    employeAutoEcoleId?: string;
  }): Promise<SchoolPaniersResponse> {
    const { data } = await http.get<SchoolPaniersResponse>(
      '/v1/integrations/rdv-permis/paniers',
      { params },
    );
    return {
      data: data?.data ?? [],
      pagination: data?.pagination,
    };
  },

  async deleteDebugPanier(panierId: string): Promise<void> {
    await http.delete(`/v1/integrations/rdv-permis/paniers/${panierId}`);
  },
  async fetchRequests(): Promise<RequestItem[]> {
    const { data } = await http.get<RequestItem[]>('/v1/requests');
    return data ?? [];
  },

  /** Ce qui bloque la réservation d'une demande, lu à l'ouverture de sa fiche. */
  async fetchRequestDiagnostic(requestId: string): Promise<RequestDiagnostic> {
    const { data } = await http.get<RequestDiagnostic>(
      `/v1/requests/${requestId}/diagnostic`,
    );
    return data;
  },

  async createRequest(payload: CreateRequestPayload): Promise<RequestItem[]> {
    const { data } = await http.post<RequestItem[]>('/v1/requests', payload);
    return data;
  },

  async resyncRequest(requestId: string): Promise<RequestItem> {
    const { data } = await http.post<RequestItem>(
      `/v1/requests/${requestId}/resync`,
    );
    return data;
  },

  async pauseRequest(requestId: string): Promise<RequestItem> {
    const { data } = await http.post<RequestItem>(
      `/v1/requests/${requestId}/pause`,
    );
    return data;
  },

  async resumeRequest(requestId: string): Promise<RequestItem> {
    const { data } = await http.post<RequestItem>(
      `/v1/requests/${requestId}/resume`,
    );
    return data;
  },

  async updateRequest(
    requestId: string,
    payload: CreateRequestPayload,
  ): Promise<RequestItem> {
    const { data } = await http.patch<RequestItem>(
      `/v1/requests/${requestId}`,
      payload,
    );
    return data;
  },

  async deleteRequest(requestId: string): Promise<void> {
    await http.delete(`/v1/requests/${requestId}`);
  },

  // ── Véhicules ────────────────────────────────────────────────────────────

  async fetchVehicles(params?: {
    availableFrom?: string;
    availableTo?: string;
    status?: string;
    vehicleType?: string;
  }): Promise<VehiclesResponse> {
    const { data } = await http.get<VehiclesResponse>('/v1/vehicles', {
      params,
    });
    return data;
  },

  async createVehicle(payload: VehiclePayload): Promise<Vehicle> {
    const { data } = await http.post<Vehicle>('/v1/vehicles', payload);
    return data;
  },

  async updateVehicle(
    id: string,
    payload: Partial<VehiclePayload>,
  ): Promise<Vehicle> {
    const { data } = await http.patch<Vehicle>(`/v1/vehicles/${id}`, payload);
    return data;
  },

  // ── Leçons & Disponibilité véhicules ─────────────────────────────────────

  async fetchInstructorWeekTemplate(
    instructorId: string,
  ): Promise<InstructorWeekTemplateEvent[]> {
    const { data } = await http.get<InstructorWeekTemplateEvent[]>(
      `/v1/lessons/week-template?instructorId=${encodeURIComponent(instructorId)}`,
    );
    return data;
  },

  async saveInstructorWeekTemplate(
    instructorId: string,
    events: Omit<InstructorWeekTemplateEvent, 'id'>[],
  ): Promise<InstructorWeekTemplateEvent[]> {
    const { data } = await http.post<InstructorWeekTemplateEvent[]>(
      '/v1/lessons/week-template',
      { instructorId, events },
    );
    return data;
  },

  async applyInstructorWeekTemplate(payload: {
    instructorId: string;
    instructorName: string;
    fromDate: string;
    toDate: string;
  }): Promise<{
    created: number;
    skipped: { date: string; reason: string }[];
  }> {
    const { data } = await http.post<{
      created: number;
      skipped: { date: string; reason: string }[];
    }>('/v1/lessons/week-template/apply', payload);
    return data;
  },

  async fetchLessonsStats(params: {
    fromDate?: string;
    toDate?: string;
    vehicleType?: string;
  }): Promise<LessonsStats> {
    const { data } = await http.get<LessonsStats>('/v1/lessons/stats', {
      params,
    });
    return data;
  },

  async fetchInstructorAvailability(
    date: string,
    vehicleType?: string,
  ): Promise<InstructorAvailability[]> {
    const params = new URLSearchParams({ date });
    if (vehicleType) params.set('vehicleType', vehicleType);
    const { data } = await http.get<InstructorAvailability[]>(
      `/v1/lessons/instructor-availability?${params}`,
    );
    return data;
  },

  async fetchLessons(params: {
    fromDate?: string;
    toDate?: string;
    vehicleType?: string;
    candidateId?: string;
  }): Promise<Lesson[]> {
    const { data } = await http.get<Lesson[]>(params.candidateId ? '/v1/lessons' : '/v1/lessons/planning-window', { params });
    return data;
  },

  async fetchCandidateAvailability(
    date: string,
    vehicleType?: string,
  ): Promise<CandidateAvailability[]> {
    const params = new URLSearchParams({ date });
    if (vehicleType) params.set('vehicleType', vehicleType);
    const { data } = await http.get<CandidateAvailability[]>(
      `/v1/lessons/candidate-availability?${params}`,
    );
    return data;
  },

  async fetchVehicleAvailability(
    date: string,
    vehicleType?: string,
  ): Promise<VehicleAvailability[]> {
    const params = new URLSearchParams({ date });
    if (vehicleType) params.set('vehicleType', vehicleType);
    const { data } = await http.get<VehicleAvailability[]>(
      `/v1/lessons/vehicle-availability?${params}`,
    );
    return data;
  },

  async createLesson(payload: CreateLessonPayload): Promise<Lesson> {
    payload.requestId ??= crypto.randomUUID();
    const { data } = await http.post<Lesson>('/v1/lessons', payload);
    return data;
  },

  /** Leçon à plusieurs élèves : créées ensemble ou pas du tout. */
  async createLessonGroup(payload: CreateLessonGroupPayload): Promise<Lesson[]> {
    payload.requestId ??= crypto.randomUUID();
    const { data } = await http.post<Lesson[]>('/v1/lessons/group', payload);
    return data;
  },

  async updateLesson(
    id: string,
    payload: UpdateLessonPayload,
  ): Promise<Lesson> {
    const { data } = await http.patch<Lesson>(`/v1/lessons/${id}`, payload);
    return data;
  },

  async deleteLesson(id: string): Promise<void> {
    await http.delete(`/v1/lessons/${id}`);
  },

  async createRecurringLesson(
    payload: CreateRecurringLessonPayload,
  ): Promise<RecurringLessonResult> {
    const { data } = await http.post<RecurringLessonResult>(
      '/v1/lessons/recurring',
      payload,
    );
    return data;
  },

  async exportLessons(params: {
    fromDate?: string;
    toDate?: string;
    vehicleType?: string;
  }): Promise<void> {
    const { data, headers } = await http.get<Blob>('/v1/lessons/export', {
      params,
      responseType: 'blob',
    });
    const disposition = headers['content-disposition'] ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : 'planning.csv';
    const url = URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── Réseau / Franchise ───────────────────────────────────────────────────

  async fetchNetworkOverview(): Promise<NetworkOverview> {
    const { data } = await http.get<NetworkOverview>('/v1/network/overview');
    return data;
  },

  // ── Formateurs ───────────────────────────────────────────────────────────

  async fetchPlanningInstructors(): Promise<Instructor[]> {
    const { data } = await http.get<Instructor[]>('/v1/lessons/instructors');
    return data;
  },

  /** Simulateurs déclarés à RdvPermis, en colonnes du planning comme les moniteurs. */
  async fetchPlanningSimulators(): Promise<Instructor[]> {
    const { data } = await http.get<Instructor[]>('/v1/lessons/simulators');
    return data;
  },

  async fetchInstructors(groupePermis?: string): Promise<Instructor[]> {
    const { data } = await http.get<Instructor[]>('/v1/etps/instructors', {
      params: groupePermis ? { groupePermis } : undefined,
    });
    return data;
  },

  async fetchInstructorStats(vehicleType?: string): Promise<InstructorStat[]> {
    const { data } = await http.get<InstructorStat[]>(
      '/v1/lessons/instructor-stats',
      {
        params: vehicleType ? { vehicleType } : undefined,
      },
    );
    return data;
  },

  // ── Mon équipe (moniteurs locaux) ─────────────────────────────────────────

  async syncTeamFromRdvPermis(): Promise<{created: number; linked: number; skipped: number}> {
    const {data} = await http.post('/v1/team/sync-rdvpermis');
    return data;
  },

  async fetchTeamMembers(): Promise<TeamMember[]> {
    const { data } = await http.get<TeamMember[]>('/v1/team');
    return data;
  },

  async createTeamMember(payload: TeamMemberPayload): Promise<TeamMember> {
    const { data } = await http.post<TeamMember>('/v1/team', payload);
    return data;
  },

  /** Modification partielle d'une fiche : seuls les champs envoyés changent. */
  async updateTeamMember(
    id: string,
    payload: Partial<TeamMemberPayload>,
  ): Promise<TeamMember> {
    const { data } = await http.patch<TeamMember>(`/v1/team/${id}`, payload);
    return data;
  },

  /** Active ou désactive une fiche moniteur : modification partielle, seul `active` est envoyé. */
  async setTeamMemberActive(id: string, active: boolean): Promise<TeamMember> {
    const { data } = await http.patch<TeamMember>(`/v1/team/${id}`, { active });
    return data;
  },

  async deleteTeamMember(id: string): Promise<void> {
    await http.delete(`/v1/team/${id}`);
  },

  /** Factures des moniteurs indépendants, les plus récentes d'abord. */
  async fetchInstructorInvoices(status?: 'submitted' | 'processed'): Promise<InstructorInvoice[]> {
    const { data } = await http.get<InstructorInvoice[]>('/v1/team/invoices', { params: status ? { status } : undefined });
    return data;
  },

  async downloadInstructorInvoice(id: string): Promise<Blob> {
    const { data } = await http.get(`/v1/team/invoices/${id}/file`, { responseType: 'blob' });
    return data as Blob;
  },

  async markInstructorInvoice(id: string, processed: boolean): Promise<InstructorInvoice> {
    const { data } = await http.patch<InstructorInvoice>(`/v1/team/invoices/${id}`, { processed });
    return data;
  },

  /** Envoie ou remplace la photo de profil d'une fiche (JPEG, PNG ou WebP, 2 Mo au plus). */
  async uploadTeamMemberPhoto(id: string, file: File): Promise<TeamMember> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await http.post<TeamMember>(
      `/v1/team/${id}/photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async removeTeamMemberPhoto(id: string): Promise<TeamMember> {
    const { data } = await http.delete<TeamMember>(`/v1/team/${id}/photo`);
    return data;
  },

  /**
   * Image d'une photo de profil. La route est protégée : le jeton part en en-tête,
   * ce qu'une balise `<img src>` ne fait pas ; l'image est donc lue ici puis affichée.
   */
  async fetchTeamPhoto(photoUrl: string): Promise<Blob> {
    const { data } = await http.get<Blob>(photoUrl, { responseType: 'blob' });
    return data;
  },

  // ── Comptes de connexion moniteur (User role INSTRUCTOR) ──────────────────

  async fetchInstructorAccounts(): Promise<InstructorAccount[]> {
    const { data } = await http.get<InstructorAccount[]>(
      '/v1/instructor-accounts',
    );
    return data;
  },

  async updateInstructorAccount(
    id: string,
    payload: Partial<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      isActive: boolean;
    }>,
  ): Promise<InstructorAccount> {
    const { data } = await http.patch<InstructorAccount>(
      `/v1/instructor-accounts/${id}`,
      payload,
    );
    return data;
  },

  async sendInstructorLoginLink(id: string): Promise<void> {
    await http.post(`/v1/instructor-accounts/${id}/send-link`);
  },

  // ── Demandes de documents ───────────────────────────────────────────────────

  async fetchDocumentTypes(): Promise<DocumentTypeOption[]> {
    const { data } = await http.get<DocumentTypeOption[]>(
      '/v1/document-requests/types',
    );
    return data;
  },

  async fetchDocumentRequests(candidateId: string): Promise<DocumentRequest[]> {
    const { data } = await http.get<DocumentRequest[]>(
      `/v1/candidates/${candidateId}/document-requests`,
    );
    return data;
  },

  async requestDocuments(
    candidateId: string,
    payload: { docTypes: string[]; note?: string },
  ): Promise<DocumentRequest[]> {
    const { data } = await http.post<DocumentRequest[]>(
      `/v1/candidates/${candidateId}/document-requests`,
      payload,
    );
    return data;
  },

  async cancelDocumentRequest(id: string): Promise<void> {
    await http.delete(`/v1/document-requests/${id}`);
  },

  async downloadDocumentRequestFile(id: string): Promise<Blob> {
    const { data } = await http.get<Blob>(`/v1/document-requests/${id}/file`, {
      responseType: 'blob',
    });
    return data;
  },

  async fetchCandidateDossier(
    candidateId: string,
  ): Promise<import('../types').CandidateDossier> {
    const { data } = await http.get<import('../types').CandidateDossier>(
      `/v1/candidates/${candidateId}/dossier`,
    );
    return data;
  },

  async uploadDossierDocument(
    candidateId: string,
    docType: string,
    file: File,
  ): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await http.post(
      `/v1/candidates/${candidateId}/dossier/${encodeURIComponent(docType)}/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  async setDocumentRequestStatus(
    id: string,
    status: 'validated' | 'rejected' | 'provided',
    version: number,
  ): Promise<void> {
    await http.post(`/v1/document-requests/${id}/status`, { status, version });
  },

  // ── Stripe Connect (paiements en ligne des élèves) ──────────────────────────

  async fetchStripeConnectStatus(): Promise<{
    connected: boolean;
    chargesEnabled: boolean;
    detailsSubmitted: boolean;
    accountId: string | null;
  }> {
    const { data } = await http.get('/v1/connect/status');
    return data;
  },

  async createStripeAccountLink(returnUrl: string): Promise<{ url: string }> {
    const { data } = await http.post<{ url: string }>(
      '/v1/connect/account-link',
      {
        returnUrl,
        refreshUrl: returnUrl,
      },
    );
    return data;
  },

  /** Moyens de paiement en ligne proposés aux élèves (J42) et leur état chez Stripe. */
  async fetchOnlinePaymentMethods(): Promise<OnlinePaymentMethod[]> {
    const { data } = await http.get<OnlinePaymentMethod[]>('/v1/connect/payment-methods');
    return data;
  },

  /** Active ou désactive des moyens de paiement en ligne ; au moins un reste activé. */
  async updateOnlinePaymentMethods(methods: string[]): Promise<OnlinePaymentMethod[]> {
    const { data } = await http.put<OnlinePaymentMethod[]>('/v1/connect/payment-methods', { methods });
    return data;
  },

  // ── Catalogue ─────────────────────────────────────────────────────────────

  async fetchCatalogMeta(): Promise<CatalogMeta> {
    const { data } = await http.get<CatalogMeta>('/v1/catalog/meta');
    return data;
  },

  async fetchCatalogItems(): Promise<CatalogItem[]> {
    const { data } = await http.get<CatalogItem[]>('/v1/catalog/items');
    return data ?? [];
  },

  async createCatalogItem(
    payload: CreateCatalogItemPayload,
  ): Promise<CatalogItem> {
    const { data } = await http.post<CatalogItem>('/v1/catalog/items', payload);
    return data;
  },

  async updateCatalogItem(
    id: string,
    payload: Partial<CreateCatalogItemPayload>,
  ): Promise<CatalogItem> {
    const { data } = await http.patch<CatalogItem>(
      `/v1/catalog/items/${id}`,
      payload,
    );
    return data;
  },

  /** Retire l'article de la vente (archivage : il reste en base pour les ventes passées). */
  async archiveCatalogItem(id: string): Promise<CatalogItem> {
    const { data } = await http.post<CatalogItem>(
      `/v1/catalog/items/${id}/archive`,
    );
    return data;
  },

  async fetchCatalogFormulas(): Promise<CatalogFormula[]> {
    const { data } = await http.get<CatalogFormula[]>('/v1/catalog/formulas');
    return data ?? [];
  },

  async createCatalogFormula(
    payload: CreateCatalogFormulaPayload,
  ): Promise<CatalogFormula> {
    const { data } = await http.post<CatalogFormula>(
      '/v1/catalog/formulas',
      payload,
    );
    return data;
  },

  async updateCatalogFormula(
    id: string,
    payload: Partial<CreateCatalogFormulaPayload>,
  ): Promise<CatalogFormula> {
    const { data } = await http.patch<CatalogFormula>(
      `/v1/catalog/formulas/${id}`,
      payload,
    );
    return data;
  },

  /** Retire la formule de la vente (archivage). */
  async archiveCatalogFormula(id: string): Promise<CatalogFormula> {
    const { data } = await http.post<CatalogFormula>(
      `/v1/catalog/formulas/${id}/archive`,
    );
    return data;
  },

  /** Articles et formules retirés de la vente, les derniers retirés d'abord. */
  async fetchArchivedCatalog(): Promise<{
    items: CatalogItem[];
    formulas: CatalogFormula[];
  }> {
    const { data } = await http.get<{
      items: CatalogItem[];
      formulas: CatalogFormula[];
    }>('/v1/catalog/archived');
    return { items: data?.items ?? [], formulas: data?.formulas ?? [] };
  },

  /** Remet en vente un article retiré, avec son prix et sa TVA. */
  async restoreCatalogItem(id: string): Promise<CatalogItem> {
    const { data } = await http.post<CatalogItem>(
      `/v1/catalog/items/${id}/restore`,
    );
    return data;
  },

  /** Remet en vente une formule retirée. */
  async restoreCatalogFormula(id: string): Promise<CatalogFormula> {
    const { data } = await http.post<CatalogFormula>(
      `/v1/catalog/formulas/${id}/restore`,
    );
    return data;
  },

  // ── Devis ─────────────────────────────────────────────────────────────────

  async fetchBillingCandidates(): Promise<BillingCandidate[]> {
    const { data } = await http.get<BillingCandidate[]>(
      '/v1/quotes/candidates',
    );
    return data ?? [];
  },

  async fetchQuotes(query?: ListDocumentsQuery): Promise<Quote[]> {
    const { data } = await http.get<Quote[]>('/v1/quotes', { params: query });
    return data ?? [];
  },

  async fetchOrders(query?: ListDocumentsQuery): Promise<Quote[]> {
    return (await http.get<Quote[]>('/v1/quotes/orders', { params: query })).data;
  },

  /** Commande enregistrée prête à confirmer ; `revisesQuoteId` : nouvelle version qui remplace celle-ci. */
  async createOrder(payload: CreateQuotePayload & { revisesQuoteId?: string }): Promise<Quote> {
    return (await http.post<Quote>('/v1/quotes/orders', payload)).data;
  },

  async fetchQuote(id: string): Promise<Quote> {
    const { data } = await http.get<Quote>(`/v1/quotes/${id}`);
    return data;
  },

  async createQuote(payload: CreateQuotePayload): Promise<Quote> {
    const { data } = await http.post<Quote>('/v1/quotes', payload);
    return data;
  },

  async updateQuote(
    id: string,
    payload: Partial<CreateQuotePayload> & { status?: string; expectedVersion: number },
  ): Promise<Quote> {
    const { data } = await http.patch<Quote>(`/v1/quotes/${id}`, payload);
    return data;
  },

  /** Aperçu de l'acceptation d'un devis : contenu figé, empreinte et avenants possibles (lecture seule). */
  async quoteAcceptancePreview(id: string): Promise<CommercialAgreementDetail> {
    return (await http.get<CommercialAgreementDetail>(`/v1/quotes/${id}/acceptance-preview`)).data;
  },
  async agreementHours(id: string): Promise<import('../types').AgreementHoursLine[]> {
    return (await http.get(`/v1/quotes/${id}/hours-corrections`)).data;
  },
  async correctAgreementHours(
    id: string,
    body: { requestId: string; reason: string; lines: Array<{ quoteItemId: string; hoursPerUnit: string }> },
  ): Promise<{ corrected: number }> {
    return (await http.post(`/v1/quotes/${id}/hours-corrections`, body)).data;
  },
  async ordersWithoutHours(): Promise<import('../types').OrderWithoutHours[]> {
    return (await http.get('/v1/quotes/orders/without-hours')).data;
  },
  async quoteAgreement(id: string): Promise<CommercialAgreementDetail> {
    return (await http.get<CommercialAgreementDetail>(`/v1/quotes/${id}/agreement`)).data;
  },
  async invoiceAgreement(id: string, requestId: string): Promise<{invoiceId: string}> {
    return (await http.post(`/v1/quotes/${id}/invoice`, {requestId})).data;
  },
  /** Factures payées de l'élève qui reprennent exactement la commande, encore non facturée. */
  async attachableInvoices(id: string): Promise<AttachableInvoice[]> {
    return (await http.get(`/v1/quotes/${id}/attachable-invoices`)).data;
  },
  /** Rattache une facture payée qui règle la commande (elle n'est pas modifiée). */
  async attachInvoice(id: string, requestId: string, invoiceId: string): Promise<{invoiceId: string}> {
    return (await http.post(`/v1/quotes/${id}/attach-invoice`, {requestId, invoiceId})).data;
  },
  async partialInvoiceAgreement(id: string, requestId: string, quantities: Record<string,string>): Promise<{invoiceId: string}> {
    return (await http.post(`/v1/quotes/${id}/partial-invoice`, {requestId, quantities})).data;
  },
  async depositInvoiceAgreement(id:string,requestId:string,amountTtc:string):Promise<{invoiceId:string}>{
    return (await http.post(`/v1/quotes/${id}/deposit-invoice`,{requestId,amountTtc})).data;
  },
  async reduceAgreement(id:string,body:FormData):Promise<{reductionId:string}>{
    return (await http.post(`/v1/quotes/${id}/reductions`,body,{headers:{'Content-Type':'multipart/form-data'}})).data;
  },
  async downloadReductionEvidence(id:string,reductionId:string):Promise<void>{
    const {data}=await http.get<Blob>(`/v1/quotes/${id}/reductions/${reductionId}/evidence`,{responseType:'blob'});
    const url=URL.createObjectURL(data),a=document.createElement('a');a.href=url;a.download='avenant-reduction.pdf';a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
  },
  async archiveAgreement(id: string, requestId: string): Promise<{archiveId: string}> {
    return (await http.post(`/v1/quotes/${id}/commercial-archives`, {requestId})).data;
  },
  async downloadCommercialArchive(id: string, archiveId: string): Promise<void> {
    const {data} = await http.get<Blob>(`/v1/quotes/${id}/commercial-archives/${archiveId}`, {responseType:'blob'});
    const url = URL.createObjectURL(data); const a = document.createElement('a');
    a.href = url; a.download = 'dossier-commercial.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  async downloadAcceptanceEvidence(id: string): Promise<void> {
    const {data}=await http.get<Blob>(`/v1/quotes/${id}/acceptance-evidence`, {responseType:'blob'});
    const url=URL.createObjectURL(data); const anchor=document.createElement('a');
    anchor.href=url; anchor.download='justificatif-acceptation.pdf'; anchor.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000);
  },

  async reviseQuote(id: string): Promise<Quote> {
    const { data } = await http.post<Quote>(`/v1/quotes/${id}/revise`);
    return data;
  },

  /** Commande antérieure encore « à confirmer » : confirmée par le bureau, sans signature (22/10/2026). */
  async confirmOrder(id: string): Promise<Quote> {
    const { data } = await http.post<Quote>(`/v1/quotes/${id}/confirm-order`);
    return data;
  },

  /**
   * Annule un devis ou une commande (le document est conservé). Une commande ferme ne s'annule que
   * tant que rien n'est facturé, encaissé ni consommé ; l'annulation est tracée.
   */
  async cancelQuote(id: string): Promise<Quote> {
    const { data } = await http.post<Quote>(`/v1/quotes/${id}/cancel`);
    return data;
  },

  /** `kind` : « devis », « commande » ou « brouillon » (nom du fichier, F46). */
  async downloadQuotePdf(id: string, number: string, kind = 'devis'): Promise<void> {
    const { data } = await http.get<Blob>(`/v1/quotes/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'application/pdf' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}_${number}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  /** Export CSV des devis ou des commandes affichés (filtre de statut de l'écran compris, F58). */
  async exportQuotesCsv(query?: ExportDocumentsQuery, kind: 'quotes' | 'orders' = 'quotes'): Promise<void> {
    const { data } = await http.get<Blob>(kind === 'orders' ? '/v1/quotes/orders/export' : '/v1/quotes/export', {
      params: query,
      responseType: 'blob',
    });
    const filename = kind === 'orders' ? 'commandes_export.csv' : 'devis_export.csv';
    const url = URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },


  // ── Factures ──────────────────────────────────────────────────────────────

  async fetchInvoices(query?: ListDocumentsQuery): Promise<Invoice[]> {
    const { data } = await http.get<Invoice[]>('/v1/invoices', {
      params: query,
    });
    return data ?? [];
  },

  async fetchInvoice(id: string): Promise<Invoice> {
    const { data } = await http.get<Invoice>(`/v1/invoices/${id}`);
    return data;
  },

  /** Brouillon : seule son échéance se modifie (une facture se prépare depuis sa commande). */
  async updateInvoice(
    id: string,
    payload: { paymentDeadline: string | null },
  ): Promise<Invoice> {
    const { data } = await http.patch<Invoice>(`/v1/invoices/${id}`, payload);
    return data;
  },

  /** Émet un avoir sur une facture émise (seul moyen de corriger une facture). */
  async creditBalance(id:string):Promise<{invoice:Invoice;remaining:Record<string,string>;unavailable:string|null}>{return (await http.get(`/v1/invoices/${id}/credit-balance`)).data;},
  async createPartialCredit(id:string,reason:string,quantities:Record<string,string>,requestId:string):Promise<Invoice>{return (await http.post(`/v1/invoices/${id}/credit-note`,{reason,quantities,requestId})).data;},
  async createCreditNote(id: string, reason: string): Promise<Invoice> {
    const { data } = await http.post<Invoice>(
      `/v1/invoices/${id}/credit-note`,
      { reason },
    );
    return data;
  },

  async cancelInvoice(id: string): Promise<Invoice> {
    const { data } = await http.post<Invoice>(`/v1/invoices/${id}/cancel`);
    return data;
  },

  async refundInvoice(
    invoiceId: string,
    amount: number | undefined,
    reason: string,
  ): Promise<{ refunded: number; refundId: string | null; status: string }> {
    const key = `permio-refund:${invoiceId}`;
    const previous = sessionStorage.getItem(key);
    const request = previous
      ? (JSON.parse(previous) as {
          requestId: string;
          amount?: number;
          reason: string;
        })
      : { requestId: crypto.randomUUID(), amount, reason };
    if (previous && request.amount !== amount)
      throw new Error(
        'Une demande de remboursement reste à vérifier pour cette facture. Reprenez le même montant.',
      );
    if (previous && request.reason !== undefined && request.reason !== reason)
      throw new Error('Reprenez le motif de la demande en attente.');
    request.reason ??= reason;
    sessionStorage.setItem(key, JSON.stringify(request));
    try {
      const { data } = await http.post<{
        refunded: number;
        refundId: string | null;
        status: string;
      }>(`/v1/connect/invoices/${invoiceId}/refund`, request);
      if (['succeeded', 'failed', 'canceled'].includes(data.status))
        sessionStorage.removeItem(key);
      return data;
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (
        (error as { code?: string }).code ===
          'FINANCIAL_CONFIRMATION_CANCELLED' ||
        (status && [400, 403, 404].includes(status))
      )
        sessionStorage.removeItem(key);
      throw error;
  }

  },

  async issueInvoice(id: string): Promise<Invoice> {
    const { data } = await http.post('/v1/invoices/' + id + '/issue');
    return data;
  },

  async sendInvoiceToCandidate(id: string): Promise<Invoice> {
    const { data } = await http.post<Invoice>(`/v1/invoices/${id}/send`);
    return data;
  },

  /** `kind` : « facture », « avoir » ou « brouillon » (nom du fichier, F46). */
  async downloadInvoicePdf(id: string, number: string, kind = 'facture'): Promise<void> {
    const { data } = await http.get<Blob>(`/v1/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'application/pdf' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kind}_${number}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  /** Facture électronique : ce qui manque à l'école pour joindre le Factur-X. */
  async fetchElectronicReadiness(): Promise<ElectronicReadiness> {
    const { data } = await http.get<ElectronicReadiness>('/v1/invoices/electronic-readiness');
    return data;
  },

  /** Factur-X d'une facture émise (joint, non produit, sans objet). */
  async fetchInvoiceElectronic(id: string): Promise<InvoiceElectronic> {
    const { data } = await http.get<InvoiceElectronic>(`/v1/invoices/${id}/electronic`);
    return data;
  },

  /** XML Factur-X de la facture, tel que joint à son PDF. */
  async downloadFacturX(id: string, number: string): Promise<void> {
    const { data } = await http.get<Blob>(`/v1/invoices/${id}/factur-x.xml`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/xml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `factur-x_${number}.xml`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  async exportInvoicesCsv(query?: ExportDocumentsQuery): Promise<void> {
    const { data } = await http.get<Blob>('/v1/invoices/export', {
      params: query,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = query?.type === 'credit_note' ? 'avoirs_export.csv' : 'factures_export.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  // ── Paiements ─────────────────────────────────────────────────────────────

  async fiscalClosures(): Promise<any[]> {
    const { data } = await http.get('/v1/fiscal-periods/closures');
    return data;
  },
  /** Rappel de clôture journalière (F60) : journées à clôturer et période proposée. */
  async fiscalClosuresDue(): Promise<{ count: number; oldest: string | null; days: string[]; suggested: string }> {
    const { data } = await http.get('/v1/fiscal-periods/closures/due');
    return data;
  },
  async fiscalArchives(): Promise<any[]> {
    const { data } = await http.get('/v1/fiscal-periods/archives');
    return data;
  },
  async closeFiscalPeriod(kind: 'day' | 'month' | 'year', period: string) {
    const { data } = await http.post('/v1/fiscal-periods/closures', {
      kind,
      period,
      requestId: crypto.randomUUID(),
    });
    return data;
  },
  async createFiscalArchive(closureId: string) {
    const { data } = await http.post('/v1/fiscal-periods/archives', {
      closureId,
      requestId: crypto.randomUUID(),
    });
    return data;
  },
  async fiscalArchiveStorage(id: string, action: 'store' | 'verify-storage') {
    const { data } = await http.post(
      '/v1/fiscal-periods/archives/' + id + '/' + action,
    );
    return data;
  },
  async downloadFiscalArchive(id: string) {
    const { data } = await http.get(
      '/v1/fiscal-periods/archives/' + id + '/download',
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'archive-fiscale-' + id + '.json.gz';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  async fetchPaymentsSummary(
    query?: import('../types').ListPaymentsQuery,
  ): Promise<import('../types').PaymentsSummary> {
    const { data } = await http.get<import('../types').PaymentsSummary>(
      '/v1/payments/summary',
      { params: query },
    );
    return data;
  },

  /** Journal des encaissements ; avec `page`, une page seulement (plus récents d'abord). */
  async fetchPayments(
    query?: import('../types').ListPaymentsQuery,
    page?: { limit: number; offset: number },
  ): Promise<import('../types').Payment[]> {
    const { data } = await http.get<import('../types').Payment[]>(
      '/v1/payments',
      { params: { ...query, ...page } },
    );
    return data ?? [];
  },

  /** `confirmation` : preuve obtenue dans la fenêtre « Nouvel encaissement » ; sans elle, la fenêtre de sécurité. */
  async createPayment(
    payload: import('../types').CreatePaymentPayload,
    confirmation?: import('./manual-payment-request').ManualPaymentConfirmation,
  ): Promise<import('../types').Payment> {
    return submitManualPayment(payload, confirmation);
  },

  /** Commandes confirmées et factures émises de l'élève, avec leur reste à payer. */
  async fetchPaymentTargets(
    candidateId: string,
  ): Promise<import('../types').PaymentTarget[]> {
    const { data } = await http.get('/v1/payments/targets', {
      params: { candidateId },
    });
    return data ?? [];
  },

  async fetchStripeReconciliation(): Promise<{
    tasks: Array<{ status: string; count: number; oldest: string }>;
    scans: Array<{
      account_id: string;
      completed_at: string | null;
      errors: Record<string, string>;
    }>;
  }> {
    const { data } = await http.get('/v1/payments/stripe-reconciliation');
    return data;
  },

  /** F29 : relance les événements Stripe bloqués après cinq essais de rapprochement. */
  async retryStripeReconciliation(): Promise<{ retried: number }> {
    const { data } = await http.post('/v1/payments/stripe-reconciliation/retry');
    return data;
  },

  async manualPaymentOperation(
    id: string,
    operation: 'deposit' | 'reject' | 'refund',
    payload: {
      requestId: string;
      reason: string;
      reference: string;
      amount?: number;
      paymentType?: import('../types').PaymentType;
    },
  ): Promise<unknown> {
    const { data } = await http.post(
      `/v1/payments/${id}/${operation}`,
      payload,
    );
    return data;
  },

  /** F32 : rattache un encaissement sans pièce à une commande ou une facture, sans l'annuler. */
  async attachPayment(
    id: string,
    payload: { invoiceId?: string; orderId?: string; openingBalanceId?: string; candidateId?: string; reason: string },
  ): Promise<import('../types').Payment> {
    const { data } = await http.post(`/v1/payments/${id}/attach`, payload);
    return data;
  },

  /** F21 : opération inverse d'une remise, d'un rejet ou d'un remboursement manuel ; reprise par `requestId`. */
  async reverseManualOperation(
    id: string,
    operationId: string,
    payload: { requestId: string; reason: string },
  ): Promise<{ operationId: string; paymentId: string | null }> {
    const { data } = await http.post(
      `/v1/payments/${id}/operations/${operationId}/reverse`,
      payload,
    );
    return data;
  },

  /**
   * Annule un encaissement par contre-passation (l'écriture d'origine est
   * conservée : registre inaltérable, art. 286-I-3° bis du CGI).
   */
  async correctPayment(
    id: string,
    payload: import('../types').CreatePaymentPayload & { reason: string },
  ): Promise<unknown> {
    // F33 : une correction dont la réponse est perdue se reprend avec la même clé.
    return sendWithReplayKey('correct', { id, payload }, async (requestId) => {
      const { data } = await http.post(`/v1/payments/${id}/correct`, {
        ...payload,
        requestId,
      });
      return data;
    });
  },

  async cancelPayment(
    id: string,
    reason: string,
  ): Promise<{
    payment: import('../types').Payment;
    reversal: import('../types').Payment;
  }> {
    // F33 : une annulation dont la réponse est perdue se reprend avec la même clé.
    return sendWithReplayKey('cancel', { id, reason }, async (requestId) => {
      const { data } = await http.post<{
        payment: import('../types').Payment;
        reversal: import('../types').Payment;
      }>(`/v1/payments/${id}/cancel`, { reason, requestId });
      return data;
    });
  },

  async exportPaymentsCsv(
    query?: import('../types').ListPaymentsQuery,
  ): Promise<void> {
    const { data } = await http.get<Blob>('/v1/payments/export', {
      params: query,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'paiements_export.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Safari et Firefox : téléchargement d’abord (F59).
  },

  async fetchIntegrationStats(): Promise<{
    matching: {
      total: number;
      byResult: Array<{ result: string; reason: string; count: number }>;
      /** Cycles de recherche et tentatives depuis le dernier redémarrage (absents d'un ancien serveur). */
      runs?: { total: number; errors: number };
      attempts?: number;
    };
    panier: { failures: number };
    contracts: {
      failures: number;
      byReason: Array<{ reason: string; count: number }>;
    };
    rdvReconnects: number;
  }> {
    const { data } = await http.get('/v1/integrations/stats');
    return data;
  },

  // ── Modèles de contrats ──────────────────────────────────────────────────

  async fetchContractTemplates(): Promise<
    import('../types').ContractTemplate[]
  > {
    const { data } = await http.get<import('../types').ContractTemplate[]>(
      '/v1/contracts/templates',
    );
    return data ?? [];
  },

  async fetchContractTemplate(
    id: string,
  ): Promise<import('../types').ContractTemplate> {
    const { data } = await http.get<import('../types').ContractTemplate>(
      `/v1/contracts/templates/${id}`,
    );
    return data;
  },

  async createContractTemplate(
    payload: import('../types').CreateContractTemplatePayload,
  ): Promise<import('../types').ContractTemplate> {
    const { data } = await http.post<import('../types').ContractTemplate>(
      '/v1/contracts/templates',
      payload,
    );
    return data;
  },

  async updateContractTemplate(
    id: string,
    payload: import('../types').UpdateContractTemplatePayload,
  ): Promise<import('../types').ContractTemplate> {
    const { data } = await http.patch<import('../types').ContractTemplate>(
      `/v1/contracts/templates/${id}`,
      payload,
    );
    return data;
  },

  async deleteContractTemplate(id: string): Promise<void> {
    await http.delete(`/v1/contracts/templates/${id}`);
  },

  /** Contrats types réglementaires proposés comme point de départ d'un modèle. */
  async fetchContractSchoolSettings(): Promise<
    import('../types').ContractSchoolSettings
  > {
    const { data } = await http.get<import('../types').ContractSchoolSettings>(
      '/v1/contracts/school-settings',
    );
    // Réponse inattendue : un réglage vide, jamais un assistant bloqué.
    const values = data?.values;
    return {
      fields: Array.isArray(data?.fields) ? data.fields : [],
      values:
        values && typeof values === 'object' && !Array.isArray(values)
          ? values
          : {},
      version: Number(data?.version ?? 0) || 0,
      updatedAt: data?.updatedAt ?? null,
    };
  },

  /** `applyToTemplates` : reporter aussi les valeurs dans les modèles existants. */
  async updateContractSchoolSettings(payload: {
    version: number;
    values: Record<string, string>;
    applyToTemplates?: boolean;
  }): Promise<import('../types').ContractSchoolSettings> {
    const { data } = await http.put<import('../types').ContractSchoolSettings>(
      '/v1/contracts/school-settings',
      payload,
    );
    return data;
  },

  async fetchRegulatoryBases(): Promise<import('../types').RegulatoryBase[]> {
    const { data } = await http.get<import('../types').RegulatoryBase[]>(
      '/v1/contracts/regulatory-bases',
    );
    return Array.isArray(data) ? data : [];
  },

  /**
   * Texte complet (PDF) d'un modèle bâti sur un contrat type, avec les valeurs envoyées.
   * Rien n'est enregistré : le corps est celui de l'enregistrement d'un modèle.
   */
  async previewContractTemplate(
    payload: import('../types').CreateContractTemplatePayload,
  ): Promise<Blob> {
    const { data } = await http.post<Blob>(
      '/v1/contracts/templates/preview',
      payload,
      { responseType: 'blob' },
    );
    return data;
  },

  // ── Contrats ─────────────────────────────────────────────────────────────

  async fetchContracts(
    query?: import('../types').ListContractsQuery,
  ): Promise<import('../types').Contract[]> {
    const { data } = await http.get<import('../types').Contract[]>(
      '/v1/contracts',
      { params: query },
    );
    return data ?? [];
  },

  /** Contrat dont la signature n'a pas abouti : nouveau brouillon au même contenu, l'ancien annulé. */
  async redoContract(id: string): Promise<import('../types').Contract> {
    const { data } = await http.post<import('../types').Contract>(
      `/v1/contracts/${id}/redo`,
    );
    return data;
  },

  async fetchContract(id: string): Promise<import('../types').Contract> {
    const { data } = await http.get<import('../types').Contract>(
      `/v1/contracts/${id}`,
    );
    return data;
  },

  async createContract(
    payload: import('../types').CreateContractPayload,
  ): Promise<import('../types').Contract> {
    const { data } = await http.post<import('../types').Contract>(
      '/v1/contracts',
      payload,
    );
    return data;
  },

  async updateContract(
    id: string,
    payload: import('../types').UpdateContractPayload,
  ): Promise<import('../types').Contract> {
    const { data } = await http.patch<import('../types').Contract>(
      `/v1/contracts/${id}`,
      payload,
    );
    return data;
  },

  async deleteContract(id: string): Promise<void> {
    await http.delete(`/v1/contracts/${id}`);
  },

  async downloadContractPdf(id: string, number: string): Promise<void> {
    const { data } = await http.get<Blob>(`/v1/contracts/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([data], { type: 'application/pdf' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrat_${number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
